import { Pagination } from "@nimble/candidate-api/types";
import { Loader } from "@nimble/react-requests/components/shared/loader";
import { ResponseErrorAlert } from "@nimble/react-requests/components/shared/response-error-alert";
import { SuccessResponseType } from "@nimble/react-requests/data/response-utils";
import { UseQueryResult } from "@tanstack/react-query";
import { debounce } from "lodash";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import MultiSelect from "@/components/shared/multi-select";
import { MultiSelectProps } from "@/components/shared/multi-select/multi-select";
import { MultiSelectOption } from "@/components/shared/multi-select/multi-select.view";

export interface RemoteMultiselectProps<T = string>
  extends Omit<
    MultiSelectProps<T>,
    "options" | "title" | "loading" | "onSearchChange" | "onOpenChange"
  > {
  title: string;
  useAutocomplete: (
    ids?: string[] | null,
    search?: string | null,
  ) => UseQueryResult<
    SuccessResponseType<{
      success: true;
      data: MultiSelectOption<T>[] | undefined;
      pagination: Pagination;
      message?: string;
    }>
  >;
  debounceMs?: number;
}

function RemoteMultiselectLoading({ loading }: { loading: boolean }) {
  if (!loading) return null;

  return (
    <div
      data-slot="command-loading"
      className="relative -mt-[4rem] flex items-center justify-center py-6 bg-popover z-10"
    >
      <Loader className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

function RemoteMultiselectMore({
  hasMore,
  children,
}: React.ComponentProps<"div"> & { hasMore?: boolean }) {
  if (!hasMore) return null;

  return (
    <div
      data-slot="command-more"
      className="text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none opacity-50"
    >
      {children}
    </div>
  );
}

export default function RemoteMultiselect({
  value,
  onChange,
  title,
  useAutocomplete,
  debounceMs = 700,
  ...props
}: RemoteMultiselectProps<string>) {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const hasLoadedOnceRef = useRef(false);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((term: string) => {
        setDebouncedSearchTerm(term);
      }, debounceMs),
    [debounceMs],
  );

  useEffect(() => {
    debouncedSetSearch(searchTerm);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchTerm, debouncedSetSearch]);

  const hasSearchTerm = debouncedSearchTerm.trim().length > 0;
  const {
    data: responseJson,
    isLoading: isAutocompleteLoading,
    error,
  } = useAutocomplete(
    hasSearchTerm ? null : value,
    hasSearchTerm ? debouncedSearchTerm : null,
  );

  useEffect(() => {
    if (responseJson && !isAutocompleteLoading) {
      hasLoadedOnceRef.current = true;
    }
  }, [responseJson, isAutocompleteLoading]);

  const handleOpenChange = (open: boolean) => {
    debouncedSetSearch.cancel();
    if (open) {
      setSearchTerm("");
      setDebouncedSearchTerm("");
    }
  };

  if (error) {
    return <ResponseErrorAlert error={error} />;
  }

  const options = responseJson?.data ?? [];
  const hasMore = (responseJson?.pagination?.total_pages ?? 0) > 1;
  const triggerLoading = isAutocompleteLoading && !hasLoadedOnceRef.current;

  return (
    <MultiSelect
      value={value}
      onChange={onChange}
      loading={triggerLoading}
      onSearchChange={setSearchTerm}
      onOpenChange={handleOpenChange}
      options={options}
      title={title}
      searchLoading={isAutocompleteLoading}
      {...props}
    >
      <RemoteMultiselectLoading loading={isAutocompleteLoading} />
      <RemoteMultiselectMore hasMore={hasMore}>
        Refine search to see more
      </RemoteMultiselectMore>
    </MultiSelect>
  );
}
